import './identifier-review.css'
import './sections-inline.css'
/* eslint-disable @next/next/no-img-element -- generated from the canvas: static photos and logos exactly as designed */
/**
 * HAND-MAINTAINED. Edit this file directly.
 *
 * It began as canvas2jsx.py output from the homepage canvas (v55), and that generator is
 * retired for this file as of FRIDGE-1082. Forty-one commits edited it by hand afterwards,
 * and regenerating would delete five hundred committed lines across twenty shipped cards:
 * accessible names, prefers-reduced-motion rules, and live bindings (SITE_STATS,
 * RETAILER_INTEGRATIONS_DISPLAY, IDENTIFIER_DEFINITIONS, QUIZ_COPY, and the size and lifespan
 * quick answers from lib/refrigerator-answer-facts.ts) and whole components
 * (LocalNearYou, CanvasMovingBand) that a static design artboard cannot express. The canvas
 * is a design reference now, not a source of truth; the generator refuses to write here and
 * diverts its output to tools/_preview/ instead.
 *
 * `slots` carry the live data (rails, marquees, ladders, quiz, brands, blog, filters/parts,
 * counts) and `L` is the render-time link gate (lib/homepage-2026/links.ts resolveHref).
 */
import { Fragment, type ReactNode } from 'react'
import { A } from '../canvas-link'
import { CanvasMovingBand } from '../canvas-moving-band'
import { IDENTIFIER_DEFINITIONS } from '@/lib/identifiers'
import { QUIZ_COPY } from '@/lib/homepage-2026/quiz-copy'
import { FAMILY_OF_FOUR_QUICK_ANSWER, LIFESPAN_QUICK_ANSWER } from '@/lib/homepage-2026/quick-answers'
import { SITE_STATS, RETAILER_INTEGRATIONS_DISPLAY } from '@/lib/site-stats'
import { LocalNearYou } from '@/components/homepage-2026/local-near-you'

export type Slots = Record<string, ReactNode>
export type LinkGate = (href: string) => string | null

/** ANNOUNCEMENT STRIP */
export function SecAnnouncementStrip({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>

    </>
  )
}

/** HERO */
export function SecHero({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="k-2f9zb">
        <div className="wrap hero-layout">
          <div className="hero-intro" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <h1 className="hero-h1" style={{ margin: '0', fontSize: '42px', lineHeight: '1.1', fontWeight: '800', letterSpacing: '-0.02em', color: '#101828' }}>
              <span className="hero-l1">Find your fridge or freezer.</span>
              <br />
              <span className="k-yop39">Compare every price.</span>
            </h1>
            {' '}
            <p className="hero-description" style={{ margin: '0', fontSize: '16px', color: '#364152', fontWeight: '600' }}>
              <span className="hero-description-full">Fridge.com is the refrigerator and freezer search engine — prices from major retailers, compared in one place.</span><span className="hero-description-compact">Search models and compare retailer prices in one place.</span>
            </p>
            {' '}
            <p className="hero-census" style={{ margin: '0', fontSize: '14.5px', color: '#4b5565' }}>
              Search {SITE_STATS.products} refrigerators and freezers across {SITE_STATS.brands} brands and {RETAILER_INTEGRATIONS_DISPLAY} retailers.
            </p>
            {' '}
            <div className="k-216at">{slots['hero-search']}</div>
            {' '}
          </div>
          <div className="hero-comparison">{slots['hero-rotator']}</div>
          <div className="hero-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <A className="chip" href={L('/french-door-refrigerators')}>French Door</A>
              {' '}
              <A className="chip" href={L('/counter-depth-refrigerators')}>Counter-Depth</A>
              {' '}
              <A className="chip" href={L('/garage-refrigerators')}>Garage Ready</A>
              {' '}
              <A className="chip" href={L('/collections/refrigerators-under-1000')}>Under $1,000</A>
              {' '}
              <A className="chip" style={{ borderColor: '#b7dcc4', color: '#0e8a4d', background: '#f2fbf5' }} href={L('/collections/energy-star-appliances')}>
                <svg className="i" style={{ width: '13px', height: '13px' }}>
                  <use href="#i-leaf" />
                </svg>
                ENERGY STAR
              </A>
              {' '}
              <A className="chip" href={L('/rebates')}>
                <svg className="i" style={{ width: '13px', height: '13px', color: '#6a7282' }}>
                  <use href="#i-pin" />
                </svg>
                Local Rebates
              </A>
          </div>
        </div>
      </div>
    </>
  )
}

/** TRUST STAT BAND */
export function SecTrustStatBand({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      {/* FRIDGE-1092 (owner: "Looks sloppy"): the six stats are a flex row that wrapped into an
          uneven pile on phones. Under 800px of container width they are an even 2×3 grid — same
          icon column, same number baseline in every cell — with "How we count" on its own row. */}
      <div className="@container" style={{ background: '#ffffff', borderBottom: '1px solid #eef1f5' }}>
        <div className="wrap flex flex-wrap justify-between gap-4 @max-[800px]:grid @max-[800px]:grid-cols-2 @max-[800px]:gap-x-3 @max-[800px]:gap-y-4" style={{ paddingTop: '22px', paddingBottom: '22px' }}>
          <div className="k-1zwhw">
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-user" />
            </svg>
            <div className="k-1df1t">
              <span className="statnum">{SITE_STATS.users}</span>
              <span className="statlab">Shoppers helped</span>
            </div>
          </div>
          {' '}
          <div className="k-1zwhw">
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-tag" />
            </svg>
            <div className="k-1df1t">
              <span className="statnum">{SITE_STATS.brands}</span>
              <span className="statlab">Brands tracked</span>
            </div>
          </div>
          {' '}
          <div className="k-1zwhw">
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-compare" />
            </svg>
            <div className="k-1df1t">
              <span className="statnum">{SITE_STATS.products}</span>
              <span className="statlab">Products compared</span>
            </div>
          </div>
          {' '}
          <div className="k-1zwhw">
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-chart" />
            </svg>
            <div className="k-1df1t">
              <span className="statnum">30-Day</span>
              <span className="statlab">Price history</span>
            </div>
          </div>
          {' '}
          <div className="k-1zwhw">
            <svg className="i20">
              <use href="#i-leaf" />
            </svg>
            <div className="k-1df1t">
              <span className="statnum">ENERGY STAR</span>
              <span className="statlab">Verified picks</span>
            </div>
          </div>
          {' '}
          <div className="k-1zwhw">
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-pin" />
            </svg>
            <div className="k-1df1t">
              <span className="statnum">Local</span>
              <span className="statlab">Rebates &amp; energy costs</span>
            </div>
          </div>
          {' '}
          <A className="@max-[800px]:col-span-2 @max-[800px]:justify-self-end" href={L('/about-us/methodology')} style={{ alignSelf: 'center', fontSize: '12.5px', fontWeight: '600', color: '#0469f0', whiteSpace: 'nowrap' }}>How we count →</A>
        </div>
      </div>
    </>
  )
}

/** REAL KITCHENS BAND (people, high on page) */
export function SecRealKitchensBand({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '34px' }}>
        <div className="k-191fa">
          <div className="k-12bvw">
            <img loading="lazy" decoding="async" src="/homepage-2026/canvas/ch-family-island.jpg" alt="Family preparing a feast around the kitchen island" className="k-120l3" />
          </div>
          {' '}
          <div className="hidden sm:block" style={{ borderRadius: '12px', overflow: 'hidden', aspectRatio: '3 / 2' }}>
            <img loading="lazy" decoding="async" src="/homepage-2026/canvas/ch-costume-kids.jpg" alt="Kids in penguin and giraffe costumes playing in the kitchen" className="k-120l3" />
          </div>
          {' '}
          <div className="hidden sm:block" style={{ borderRadius: '12px', overflow: 'hidden', aspectRatio: '3 / 2' }}>
            <img loading="lazy" decoding="async" src="/homepage-2026/canvas/ch-red-retro-woman.jpg" alt="Morning coffee beside a red retro refrigerator" className="k-120l3" />
          </div>
        </div>
      </div>
    </>
  )
}

/** CATEGORY TILES (scroller, owner Round 12) */
export function SecCategoryTiles({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '34px' }}>
        <div className="k-vnj4t">
          <h2 className="sect-h" style={{ margin: '0' }}>Shop by type</h2>
          {' '}
          <span className="k-2mb5u">
            <A className="viewall" href={L('/collections/refrigerators')}>All categories</A>
            <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
            </span>
          </span>
        </div>
        {' '}
        <div className="rail catrail">
          <A className="cattile" aria-label="Shop All Refrigerators" href={L('/collections/refrigerators')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-lg-lryxc2606d-1.jpg" alt="" />
            </span>
            <span className="catlab">All Refrigerators</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop French Door refrigerators" href={L('/collections/french-door-refrigerators')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-samsung-rf32cg5900sr-1.jpg" alt="" />
            </span>
            <span className="catlab">French Door</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Side-by-Side refrigerators" href={L('/collections/side-by-side-refrigerators')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-ge-gze24nyyfs-1.jpg" alt="" />
            </span>
            <span className="catlab">Side-by-Side</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Bottom Freezer" href={L('/collections/bottom-freezer-refrigerators')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-kitchenaid-krbl133sps-1.jpg" alt="" />
            </span>
            <span className="catlab">Bottom Freezer</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Apartment Size refrigerators" href={L('/collections/apartment-refrigerators')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-ge-gle12hspss-1.jpg" alt="" />
            </span>
            <span className="catlab">Apartment Size</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Mini Fridges" href={L('/collections/mini-fridges')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-ge-gde03ggkbb-1.jpg" alt="" />
            </span>
            <span className="catlab">Mini Fridges</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Upright Freezers" href={L('/collections/upright-freezers')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-conserv-asset-1.jpg" alt="" />
            </span>
            <span className="catlab">Upright Freezers</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Chest Freezers" href={L('/collections/chest-freezers')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/muhub-chest.jpg" alt="" />
            </span>
            <span className="catlab">Chest Freezers</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Wine & Beverage fridges" href={L('/collections/wine-fridges')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-newair-nbc085hx00-1.jpg" alt="" />
            </span>
            <span className="catlab">Wine &amp; Beverage</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Ice Makers" href={L('/collections/ice-makers')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-ge-profile-xpio13swss-3.jpg" alt="" />
            </span>
            <span className="catlab">Ice Makers</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Kegerators" href={L('/collections/kegerators')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-kegco-hk38ssc-l-2-1.jpg" alt="" />
            </span>
            <span className="catlab">Kegerators</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Commercial refrigeration" href={L('/collections/commercial')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-nafcoool-asset-2.jpg" alt="" />
            </span>
            <span className="catlab">Commercial</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Retro Fridges" href={L('/collections/retro-fridges')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/retro-red-cutout.png" alt="" />
            </span>
            <span className="catlab">Retro Fridges</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Mini Freezers" href={L('/collections/mini-freezers')} style={{ textDecoration: 'none' }}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/wb-mini-freezer.jpg" alt="" />
            </span>
            <span className="catlab">Mini Freezers</span>
          </A>
        </div>
      </div>
    </>
  )
}

/** PRICE DROPS VERIFIED TODAY (real /deals rows, 09/01/2026; house card anatomy) */
export function SecPriceDropsVerifiedToday({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '30px' }}>
        <div className="k-2clpu">
          <h2 id="deals" className="sect-h" style={{ margin: '0' }}>{slots['fridge-drop-heading']}</h2>
          {' '}
          {slots['fridge-drop-link']}
        </div>
        {' '}
        <div className="sect-sub" style={{ paddingBottom: '14px' }}>{slots['fridge-drop-description']}</div>
        {' '}
        <div className="dealmarq">{slots['marquee-deals']}</div>
        {' '}
        <div className="k-28jc9">
          <h2 id="freezer-price-drops" className="sect-h" style={{ margin: '0' }}>{slots['freezer-drop-heading']}</h2>
          {' '}
          {slots['freezer-drop-link']}
        </div>
        {' '}
        <div className="sect-sub" style={{ paddingBottom: '14px' }}>{slots['freezer-drop-description']}</div>
        {' '}
        <div className="dealmarq" style={{ marginTop: '14px' }}>{slots['marquee-deals-2']}</div>
      </div>
    </>
  )
}

/** ONE-OFF ORANGE FEATURE: RETRO (brand orange BEHIND the product) */
export function SecOneOffOrangeFeatureRetro({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '30px' }}>
        <div className="k-tbq1c">
          <div className="k-76q5g">
            <A href={L('/about-us/methodology#how-we-pick')} style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7c2d12' }}>Staff favorite</A>
            {' '}
            <h2 className="k-5cuak">Retro fridges are back.</h2>
            {' '}
            <p className="k-1e16x">
              Chrome handles, rounded corners, colors that make the kitchen. Compare every retro model and every price — from countertop minis to full-size top mounts.
            </p>
            {' '}
            <div className="k-29bxm">
              <A href={L('/collections/retro-fridges')} style={{ background: '#0469f0', color: '#ffffff', fontWeight: '700', fontSize: '15px', padding: '12px 24px', borderRadius: '999px', textDecoration: 'none' }}>Shop Retro Fridges</A>
              {' '}
              <A href={L('/compare')} style={{ background: '#ffffff', color: '#0469f0', fontWeight: '700', fontSize: '15px', padding: '12px 24px', borderRadius: '999px', textDecoration: 'none' }}>Compare Prices</A>
            </div>
          </div>
          {' '}
          <img loading="lazy" decoding="async" src="/homepage-2026/canvas/retro-red-cutout.png" alt="Frigidaire retro red refrigerator" className="k-1p7n0" />
        </div>
      </div>
      <div className="wrap" style={{ paddingTop: '26px' }}>
        <div className="k-1f0l0">
          <span className="k-1webg">As Featured In</span>
          {' '}
          <CanvasMovingBand showControls={false} label="retro section publications" className="marq publication-strip">
            <div className="marq-track">
              {/* FRIDGE-92: ONE authored set. CanvasMovingBand appends the aria-hidden loop copy itself,
                  so a second authored copy only served 22 more logos that the rail never needs. */}
              <div className="k-kuxor">
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-new-york-post.png" alt="New York Post" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-yahoo.svg" alt="Yahoo" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-aol.svg" alt="AOL" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-food-and-wine.svg" alt="Food and Wine" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-mirror.svg" alt="Mirror" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-express.png" alt="Express" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-wikihow.svg" alt="WikiHow" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-chowhound.svg" alt="Chowhound" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-daily-dot.svg" alt="Daily Dot" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-the-daily-meal.svg" alt="The Daily Meal" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-first-for-women.png" alt="First for Women" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-daily-record.svg" alt="Daily Record" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-northjersey.svg" alt="northjersey.com" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-rupa-health.svg" alt="Rupa Health" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-grokipedia.png" alt="Grokipedia" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-the-fact-site.png" alt="The Fact Site" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-daily-dish.png" alt="Daily Dish" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-a-pinch-of-healthy.png" alt="A Pinch of Healthy" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-cupcakes-and-cutlery.png" alt="Cupcakes and Cutlery" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-food-huggers.png" alt="Food Huggers" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-foodnhotelasia.png" alt="Food and Hotel Asia" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-uk-com.svg" alt="uk.com" />
              </div>
            </div>
          </CanvasMovingBand>
          {' '}
          {/* FRIDGE-1110 (owner: "Present better") — the catalog numbers were one
              centered run-on wrapping into ragged lines on a phone. Even grid, 2-up on
              phones and 4-up from 640px; the numbers and wording are untouched. */}
          <div className="statgrid">
            <div><b>{SITE_STATS.products}</b><span>products</span></div>
            <div><b>{SITE_STATS.brands}</b><span>brands</span></div>
            <div><b>90</b><span>free tools</span></div>
            <div><b>25+</b><span>retailers compared</span></div>
          </div>
        </div>
      </div>
    </>
  )
}

/** EXACT-NEED COLLECTION LINKS */
export function SecExactNeedCollectionLinks({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '38px' }}>
        <div className="k-psy93">
          <h2 className="sect-h" style={{ margin: '0' }}>Shop the exact refrigerator you need</h2>
          {' '}
          <A className="viewall" href={L('/collections/all')}>View all {SITE_STATS.collections} collections</A>
        </div>
        {' '}
        <div className="k-6cvqr">
          <A className="ctile" aria-label="Shop 36 Inch Wide Refrigerators" href={L('/collections/36-inch-wide-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-ruler" />
              </svg>
            </span>
            36 Inch Wide Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop 33 Inch Wide Refrigerators" href={L('/collections/33-inch-wide-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-ruler" />
              </svg>
            </span>
            33 Inch Wide Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop 30 Inch Wide Refrigerators" href={L('/collections/30-inch-wide-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-ruler" />
              </svg>
            </span>
            30 Inch Wide Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Counter-Depth Refrigerators" href={L('/collections/counter-depth-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-min" />
              </svg>
            </span>
            Counter-Depth Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Garage-Ready Freezers" href={L('/collections/garage-freezers')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-build" />
              </svg>
            </span>
            Garage-Ready Freezers
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Side-by-Side Refrigerators" href={L('/collections/side-by-side-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-compare" />
              </svg>
            </span>
            Side-by-Side Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Panel-Ready Freezers" href={L('/collections/panel-ready-freezers')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-snow" />
              </svg>
            </span>
            Panel-Ready Freezers
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Quiet Refrigerators" href={L('/collections/quiet-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-vol" />
              </svg>
            </span>
            Quiet Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Ice Makers" href={L('/collections/ice-makers')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-drop" />
              </svg>
            </span>
            Ice Makers
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Small Kitchen Refrigerators" href={L('/collections/small-kitchen-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-home" />
              </svg>
            </span>
            Small Kitchen Refrigerators
          </A>
          {' '}
          <div className="exact-need-love-banner" style={{ gridColumn: '1 / -1', minWidth: 0, borderTop: '1px solid #eef1f5', borderBottom: '1px solid #eef1f5', padding: '10px 0', margin: '6px 0' }}>
            <CanvasMovingBand showControls={false} label="Fridges, Freezers, & Kitchen Stuff" className="marq" style={{ padding: '14px 0' }}>
              {/* FRIDGE-1273 (owner: "use the social logo we already have"), superseding FRIDGE-1194's
                  bare fridge icon: the round Fridge.com social badge — the profile picture on the
                  Pinterest account, whose ring reads FRIDGES · FREEZERS · KITCHEN STUFF, the same
                  words as this band. It sits after every repeat, so the loop seam between sets
                  carries one too. Decorative: the text is read once. */}
              <div className="marq-track" style={{ gap: '28px' }}>
                {[0, 1].map(set => (
                  <div key={set} style={{ display: 'flex', alignItems: 'center', gap: '28px', flex: 'none' }}>
                    {[0, 1, 2].map(item => (
                      <Fragment key={item}>
                        <span aria-hidden={item > 0 || undefined} style={{ whiteSpace: 'nowrap', fontSize: '18px', fontWeight: 800, color: '#0454c0' }}>Fridges, Freezers, &amp; Kitchen Stuff</span>
                        <img src="/Fridge.com-brand-logos/social-badge.png" alt="" aria-hidden="true" width={40} height={40} loading="lazy" decoding="async" style={{ flex: 'none', display: 'block' }} />
                      </Fragment>
                    ))}
                  </div>
                ))}
              </div>
            </CanvasMovingBand>
          </div>
          <A className="ctile" aria-label="Shop Apartment Refrigerators" href={L('/collections/apartment-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-home" />
              </svg>
            </span>
            Apartment Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Dorm Room Mini Fridges" href={L('/collections/mini-fridges-for-dorm-room')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-book" />
              </svg>
            </span>
            Dorm Room Mini Fridges
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Beverage Centers" href={L('/collections/beverage-coolers')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-wine" />
              </svg>
            </span>
            Beverage Centers
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Wine Fridges" href={L('/collections/wine-fridges')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-wine" />
              </svg>
            </span>
            Wine Fridges
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Commercial Refrigeration" href={L('/collections/commercial')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-build" />
              </svg>
            </span>
            Commercial Refrigeration
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Undercounter Refrigerators" href={L('/collections/undercounter-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-min" />
              </svg>
            </span>
            Undercounter Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Smart Fridges" href={L('/collections/smart-fridges')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-cpu" />
              </svg>
            </span>
            Smart Fridges
          </A>
          {' '}
          <A className="ctile" aria-label="Shop Black Stainless Refrigerators" href={L('/collections/black-stainless-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-tag" />
              </svg>
            </span>
            Black Stainless Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop White Refrigerators" href={L('/collections/white-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-tag" />
              </svg>
            </span>
            White Refrigerators
          </A>
          {' '}
          <A className="ctile" aria-label="Shop French Door Refrigerators" href={L('/collections/french-door-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-home" />
              </svg>
            </span>
            French Door Refrigerators
          </A>
        </div>
      </div>
      <div className="wrap" style={{ marginTop: '44px' }}>
        <div className="k-1rk4x">
          <div className="k-2adsu">
            <span className="k-11df8">Best Sellers</span>
            {' '}
            <span className="k-cgyv8">Make Kitchen Memories</span>
            {' '}
            <span className="k-2py0m">Find your fridge. Keep your cool.</span>
          </div>
          {' '}
          <div style={{ borderRadius: '14px', overflow: 'hidden', aspectRatio: '3 / 2', width: 'min(100%, 540px)', flex: 'none', boxShadow: '0 16px 32px -14px rgba(16, 24, 40, 0.3)' }}>
            <img loading="lazy" decoding="async" src="/homepage-2026/canvas/wb-mom-and-daughter-in-kitchen.jpg" alt="Mom and daughter making memories in the kitchen" className="k-120l3" />
          </div>
        </div>
      </div>
    </>
  )
}

/** REFRIGERATORS (scroller) */
export function SecRefrigerators({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingBottom: '4px' }}>
          <h2 id="refrigerators" className="sect-h railhead-title" style={{ margin: '0' }}>Shop Fridges</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          French door, side-by-side, bottom freezer and counter-depth — live prices compared across retailers.
        </div>
          <A className="viewall railhead-link" href={L('/collections/refrigerators')}>Shop all refrigerators</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-fridges']}</div>
      </div>
      <div className="wrap" style={{ paddingTop: '26px' }}>
        <div className="k-25w3v">
          <div className="k-157np">
            <span id="colors" className="k-1r0qj">Fridge colors</span>
            <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
            </span>
          </div>
          {' '}
          <div className="rail colorrail">
            <A className="finsw" aria-label="Stainless Steel refrigerators" href={L('/collections/stainless-steel-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-x9xe2"></span>
              <span className="k-2bk5e">Stainless Steel</span>
            </A>
            {' '}
            <A className="finsw" aria-label="White refrigerators" href={L('/collections/white-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-8vjy8"></span>
              <span className="k-2bk5e">White</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Black refrigerators" href={L('/collections/black-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-nzxfn"></span>
              <span className="k-2bk5e">Black</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Silver refrigerators" href={L('/collections/silver-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-ajba3"></span>
              <span className="k-2bk5e">Silver</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Gray refrigerators" href={L('/collections/gray-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-1v52b"></span>
              <span className="k-2bk5e">Gray</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Cream refrigerators" href={L('/collections/cream-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-13323"></span>
              <span className="k-2bk5e">Cream</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Slate refrigerators" href={L('/collections/slate-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-1erd3"></span>
              <span className="k-2bk5e">Slate</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Black Stainless refrigerators" href={L('/collections/black-stainless-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-1zug8"></span>
              <span className="k-2bk5e">Black Stainless</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Red refrigerators" href={L('/collections/red-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-22v57"></span>
              <span className="k-2bk5e">Red</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Blue refrigerators" href={L('/collections/blue-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-1angg"></span>
              <span className="k-2bk5e">Blue</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Green refrigerators" href={L('/collections/green-refrigerators')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-28ba1"></span>
              <span className="k-2bk5e">Green</span>
            </A>
          </div>
        </div>
      </div>
    </>
  )
}

/** FREEZERS (scroller) */
export function SecFreezers({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '40px' }}>
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingBottom: '4px' }}>
          <h2 id="freezers" className="sect-h railhead-title" style={{ margin: '0' }}>Shop Freezers</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>Freezer prices tracked across retailers — upright, chest and garage-ready, from compact second freezers to bulk storage.</div>
          <A className="viewall railhead-link" href={L('/collections/freezers')}>Shop freezer prices & deals</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-freezers']}</div>
      </div>
      <div className="wrap" style={{ paddingTop: '26px' }}>
        <div className="k-25w3v">
          <div className="k-157np">
            <span className="k-1r0qj">Freezer colors</span>
            <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
            </span>
          </div>
          {' '}
          <div className="rail colorrail">
            <A className="finsw" aria-label="White freezers" href={L('/collections/white-freezers')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-8vjy8"></span>
              <span className="k-2bk5e">White</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Black freezers" href={L('/collections/black-freezers')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-nzxfn"></span>
              <span className="k-2bk5e">Black</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Stainless Steel freezers" href={L('/collections/stainless-steel-freezers')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-x9xe2"></span>
              <span className="k-2bk5e">Stainless Steel</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Gray freezers" href={L('/collections/gray-freezers')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-1v52b"></span>
              <span className="k-2bk5e">Gray</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Silver freezers" href={L('/collections/silver-freezers')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-ajba3"></span>
              <span className="k-2bk5e">Silver</span>
            </A>
            {' '}
            <A className="finsw" aria-label="Red freezers" href={L('/collections/red-freezers')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span className="k-22v57"></span>
              <span className="k-2bk5e">Red</span>
            </A>
          </div>
        </div>
      </div>
    </>
  )
}

/** ONE-OFF ORANGE FEATURE: UPRIGHT FREEZERS (brand orange BEHIND the product; FRIDGE-1077 — the cutout is a full-height upright, so the band sells uprights) */
export function SecOneOffOrangeFeatureMiniFreezers({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '34px' }}>
        <div className="k-2eh2w">
          <img loading="lazy" decoding="async" src="/homepage-2026/canvas/black-freezer-cutout.png" alt="Conserv black convertible upright freezer" className="k-212cl" />
          {' '}
          <div className="k-6a451">
            <span className="k-6ijr9">Stand-up storage, small footprint</span>
            {' '}
            <h2 className="k-1uwww">A freezer that fits anywhere.</h2>
            {' '}
            <p className="k-1e16x">
              Garages, basements, offices, apartments — compact to full-size upright freezers on a cabinet-sized footprint, price-tracked across major retailers.
            </p>
            {' '}
            <div className="k-29bxm">
              <A href={L('/collections/upright-freezers')} style={{ background: '#0469f0', color: '#ffffff', fontWeight: '700', fontSize: '15px', padding: '12px 24px', borderRadius: '999px', textDecoration: 'none' }}>Shop Upright Freezers</A>
              {' '}
              <A href={L('/compare')} style={{ background: '#ffffff', color: '#0469f0', fontWeight: '700', fontSize: '15px', padding: '12px 24px', borderRadius: '999px', textDecoration: 'none' }}>Compare Prices</A>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/** COMPARE BANNER */
export function SecCompareBanner({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      {/* FRIDGE-1094 (owner: "Looks sloppy"): the four features wrapped 3 + 1 on phones. Under
          640px of container width they are an even 2×2 grid; desktop keeps four across. */}
      <div className="wrap @container" style={{ paddingTop: '44px' }}>
        <div className="k-j5mcn">
          <div className="k-l9gxb">
            <span className="k-omvvi">Instantly compare every retailer price.</span>
            {' '}
            <span className="k-24eqp">See 30-day price history before you buy — on every model.</span>
          </div>
          {' '}
          <div className="flex items-center justify-center flex-wrap gap-[clamp(16px,5vw,28px)] min-w-0 @max-[640px]:grid @max-[640px]:grid-cols-2 @max-[640px]:gap-4 @max-[640px]:w-full">
            <div className="k-oykas">
              <svg className="i20">
                <use href="#i-compare" />
              </svg>
              <span className="k-3ipcm">{RETAILER_INTEGRATIONS_DISPLAY} Retailers</span>
              <span className="k-2phkh">Compared</span>
            </div>
            {' '}
            <div className="k-oykas">
              <svg className="i20">
                <use href="#i-clock" />
              </svg>
              <span className="k-3ipcm">Prices</span>
              <span className="k-2phkh">Updated daily</span>
            </div>
            {' '}
            <div className="k-oykas">
              <svg className="i20">
                <use href="#i-chart" />
              </svg>
              <span className="k-3ipcm">Price History</span>
              <span className="k-2phkh">30-day tracking</span>
            </div>
            {' '}
            <div className="k-oykas">
              <svg className="i20">
                <use href="#i-tag" />
              </svg>
              <span className="k-3ipcm">Rebates</span>
              <span className="k-2phkh">Save more</span>
            </div>
          </div>
          {' '}
          <span className="k-2kq50">Compare Prices</span>
        </div>
      </div>
    </>
  )
}

/** BEST BRANDS RIBBON (heritage element) */
export function SecBestBrandsRibbon({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="k-2dm66">
        <CanvasMovingBand showControls={false} label="brand highlights" className="marq" style={{ padding: '14px 0' }}>
          <div className="marq-track" style={{ gap: '56px', animationDuration: '35s' }}>
            <div className="k-6cbmb">
              <span className="k-10yr2">
                <svg className="k-1eugr">
                  <use href="#i-globe" />
                </svg>
                <span className="k-2omh4">The Best Brands In The World</span>
              </span>
              {' '}
              <span className="k-10yr2">
                <svg className="k-1eugr">
                  <use href="#i-globe" />
                </svg>
                <span className="k-2omh4">The Best Brands In The World</span>
              </span>
              {' '}
              <span className="k-10yr2">
                <svg className="k-1eugr">
                  <use href="#i-globe" />
                </svg>
                <span className="k-2omh4">The Best Brands In The World</span>
              </span>
            </div>
            {' '}
            <div className="k-6cbmb" aria-hidden="true">
              <span className="k-10yr2">
                <svg className="k-1eugr">
                  <use href="#i-globe" />
                </svg>
                <span className="k-2omh4">The Best Brands In The World</span>
              </span>
              {' '}
              <span className="k-10yr2">
                <svg className="k-1eugr">
                  <use href="#i-globe" />
                </svg>
                <span className="k-2omh4">The Best Brands In The World</span>
              </span>
              {' '}
              <span className="k-10yr2">
                <svg className="k-1eugr">
                  <use href="#i-globe" />
                </svg>
                <span className="k-2omh4">The Best Brands In The World</span>
              </span>
            </div>
          </div>
        </CanvasMovingBand>
      </div>
    </>
  )
}

/** SHOP BY BRAND */
export function SecShopByBrand({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        <div className="k-psy93">
          <h2 id="brands" className="sect-h" style={{ margin: '0' }}>Shop by brand</h2>
          {' '}
          <A className="viewall" href={L('/brands')}>View all {SITE_STATS.brands} brands</A>
        </div>
        {' '}
        <div className="home-brands">{slots['brands-grid']}</div>
      </div>
    </>
  )
}

/** SHOP BY BUDGET / WIDTH / FINISH */
export function SecShopByBudgetWidthFinish({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '34px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {/* FRIDGE-1095 (owner: "Looks sloppy"): heading and chips shared one wrapping row, so on
            phones the heading read as the first chip and the rest wrapped ragged. Under 640px of
            container width the heading takes its own line and the chips are ONE horizontal scroll
            row with a right-edge fade (docs/Design.md §22.2); from 640px the one-line layout stays. */}
        <div className="@container">
        <div className="flex items-center gap-[14px] flex-wrap @max-[640px]:flex-col @max-[640px]:items-stretch @max-[640px]:gap-2">
          <span className="k-xx9fn">Shop by budget</span>
          {' '}
          <div className="chipset">
          <A className="chip" style={{ background: '#f0f6ff', borderColor: '#cfe1fb', color: '#0454c0', fontWeight: '600' }} href={L('/collections/refrigerators-under-500')}>Under $500</A>
          {' '}
          <A className="chip" style={{ background: '#f0f6ff', borderColor: '#cfe1fb', color: '#0454c0', fontWeight: '600' }} href={L('/collections/refrigerators-under-1000')}>Under $1,000</A>
          {' '}
          <A className="chip" style={{ background: '#f0f6ff', borderColor: '#cfe1fb', color: '#0454c0', fontWeight: '600' }} href={L('/collections/refrigerators-under-2000')}>Under $2,000</A>
          {' '}
          <A className="chip" style={{ background: '#f0f6ff', borderColor: '#cfe1fb', color: '#0454c0', fontWeight: '600' }} href={L('/collections/refrigerators-under-3000')}>Under $3,000</A>
          {' '}
          <A className="chip" style={{ background: '#f0f6ff', borderColor: '#cfe1fb', color: '#0454c0', fontWeight: '600' }} href={L('/luxury-refrigerators')}>Luxury $3,000+</A>
          </div>
        </div>
        </div>
        {' '}
        <div className="@container">
        <div className="flex items-center gap-[5px] flex-wrap @max-[640px]:flex-col @max-[640px]:items-stretch @max-[640px]:gap-2">
          <span className="k-20tpn">Shop by width</span>
          {' '}
          <div className="chipset">
          <A className="chip" href={L('/collections/18-inch-wide-refrigerators')}>18 in</A>
          <A className="chip" href={L('/collections/21-inch-wide-refrigerators')}>21 in</A>
          <A className="chip" href={L('/collections/24-inch-wide-refrigerators')}>24 in</A>
          <A className="chip" href={L('/collections/27-inch-wide-refrigerators')}>27 in</A>
          <A className="chip" href={L('/collections/28-inch-wide-refrigerators')}>28 in</A>
          <A className="chip" href={L('/collections/30-inch-wide-refrigerators')}>30 in</A>
          <A className="chip" href={L('/collections/32-inch-wide-refrigerators')}>32 in</A>
          <A className="chip" href={L('/collections/33-inch-wide-refrigerators')}>33 in</A>
          <A className="chip" href={L('/collections/34-inch-wide-refrigerators')}>34 in</A>
          <A className="chip" href={L('/collections/35-inch-wide-refrigerators')}>35 in</A>
          <A className="chip" href={L('/collections/36-inch-wide-refrigerators')}>36 in</A>
          <A className="chip" href={L('/counter-depth-refrigerators')}>Counter-Depth</A>
          <A className="chip" href={L('/built-in-refrigerators')}>Built-In</A>
          </div>
        </div>
        </div>
      </div>
    </>
  )
}

/** SPECIALTY COOLING (tabbed: Mini · Wine · Ice · Commercial) */
export function SecSpecialtyCooling({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" id="specialty" style={{ paddingTop: '44px' }}>
        <div className="k-1y7ip">
          <h2 className="sect-h" style={{ margin: '0' }}>Shop Specialty refrigeration</h2>
          {' '}
          <div role="tablist" aria-label="Specialty refrigeration" className="k-o4nb1">
            <button type="button" role="tab" className="spectab">
              <svg className="i">
                <use href="#i-wine" />
              </svg>
              Wine &amp; Beverage
            </button>
            {' '}
            <button type="button" role="tab" className="spectab">
              <svg className="i">
                <use href="#i-min" />
              </svg>
              Mini Fridges
            </button>
            {' '}
            <button type="button" role="tab" className="spectab">
              <svg className="i">
                <use href="#i-snow" />
              </svg>
              Ice Makers
            </button>
            {' '}
            <button type="button" role="tab" className="spectab">
              <svg className="i">
                <use href="#i-build" />
              </svg>
              Commercial
            </button>
            {' '}
            <button type="button" role="tab" className="spectab">
              <svg className="i">
                <use href="#i-drop" />
              </svg>
              Kegerators
            </button>
          </div>
        </div>
        {' '}
        {slots['specialty-panels']}
      </div>
    </>
  )
}

/** FILTERS & PARTS (the two catalog families, FFN / FPN) */
export function SecFiltersParts({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        <div className="k-2clpu">
          <h2 className="sect-h" style={{ margin: '0' }}>Filters &amp; Parts</h2>
          {' '}
          <A className="viewall" href={L('/parts')}>Shop the catalogs</A>
        </div>
        {' '}
        <div className="sect-sub" style={{ paddingBottom: '14px' }}>
          The right filter or replacement part for your exact model — every one carries its own permanent ID.
        </div>
        {' '}
        <div className="k-2p9i0">
          <div className="k-2053z">
            <div className="k-1xrsu">
              <span className="k-2pliq">FFN</span>
              <span className="k-2kw43">Refrigerator &amp; Freezer Filters</span>
            </div>
            {' '}
            {slots['filters-rows']}
            {' '}
            <A href={L('/filters')} style={{ paddingTop: '12px', fontSize: '13px', fontWeight: '700' }}>Shop all filters →</A>
          </div>
          {' '}
          <div className="k-2053z">
            <div className="k-1xrsu">
              <span className="k-1u01l">FPN</span>
              <span className="k-2kw43">Replacement Parts</span>
            </div>
            {' '}
            {slots['parts-rows']}
            {' '}
            <A href={L('/parts')} style={{ paddingTop: '12px', fontSize: '13px', fontWeight: '700' }}>Shop all parts →</A>
          </div>
        </div>
      </div>
    </>
  )
}

/** FAMILY & FRIENDS — FUN MOMENTS (heritage section, original photos) */
export function SecFamilyFriendsFunMoments({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '48px' }}>
        <div className="k-1by0y">
          <span className="k-1wjzr">Family &amp; Friends</span>
          {' '}
          <h2 className="k-269yi">Be Ready For The Fun Moments</h2>
        </div>
        {' '}
        <div className="k-oq3d5">
          <A className="funcard" href={L('/collections/ice-makers')}>
            <div className="k-12bvw">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/wb-friends.jpg" alt="Friends pouring drinks in the kitchen" className="k-120l3" />
            </div>
            {' '}
            <span className="funtitle">Ice Makers</span>
            {' '}
            <span className="fundesc">Crisp nugget or cube ice on demand, so every party drink is poured over fresh ice.</span>
          </A>
          {' '}
          <A className="funcard" href={L('/collections/wine-fridges')}>
            <div className="k-12bvw">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/wb-wine-party.jpg" alt="Friends gathered around wine and a charcuterie board" className="k-120l3" />
            </div>
            {' '}
            <span className="funtitle">Wine Fridges</span>
            {' '}
            <span className="fundesc">Every bottle held at its perfect temperature, ready to uncork when friends arrive.</span>
          </A>
          {' '}
          <A className="funcard" href={L('/collections/beer-fridges')}>
            <div className="k-12bvw">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/wb-beer-party.jpg" alt="Friends toasting with cold drinks" className="k-120l3" />
            </div>
            {' '}
            <span className="funtitle">Beer Fridges</span>
            {' '}
            <span className="fundesc">Cold brews for game day and casual hangouts, in a fridge that is always stocked.</span>
          </A>
        </div>
      </div>
    </>
  )
}

/** PRICE INTELLIGENCE (mirror of the Promise split: visual left, text right) */
export function SecPriceIntelligence({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '48px' }}>
        <div className="k-2kzql">
          <div className="k-bi02k">
            <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', boxShadow: '0 18px 36px -18px rgba(16, 24, 40, 0.25)', transform: 'rotate(-1.2deg)' }}>{slots['pi-check']}</div>
            {' '}
            <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', boxShadow: '0 18px 36px -18px rgba(16, 24, 40, 0.25)', transform: 'rotate(1deg)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span className="k-1km8m">Best Prices Right Now</span>
              {' '}
              <div className="rotwrap pi-rot">{slots['pi-ladders']}</div>
            </div>
          </div>
          {' '}
          <div className="k-25w3v">
            <span className="k-1wjzr">Live Price Intelligence</span>
            {' '}
            <h2 className="k-269yi">Never overpay again.</h2>
            {' '}
            <div className="k-25jwa">
              <svg className="i20" style={{ color: '#0469f0', flex: 'none' }}>
                <use href="#i-chart" />
              </svg>
              <span className="k-k4zgf">Today’s prices, checked against recorded price history.</span>
            </div>
            {' '}
            <div className="k-25jwa">
              <svg className="i20" style={{ color: '#0469f0', flex: 'none' }}>
                <use href="#i-compare" />
              </svg>
              <span className="k-k4zgf">Retailer leaders re-ranked as prices move, all day long.</span>
            </div>
            {' '}
            <div className="k-25jwa">
              <svg className="i20" style={{ color: '#0469f0', flex: 'none' }}>
                <use href="#i-tag" />
              </svg>
              <span className="k-k4zgf">A deal is only a deal against the tracked median — never a made-up list price.</span>
            </div>
            {' '}
            <span className="k-1h5ny">Watch the price. We already are.</span>
          </div>
        </div>
      </div>
      <div className="wrap" style={{ paddingTop: '34px' }}>
        <div className="k-1f0l0">
          <span className="k-1webg">As Featured In</span>
          {' '}
          <CanvasMovingBand showControls={false} label="deals section publications" className="marq publication-strip">
            <div className="marq-track">
              {/* FRIDGE-92: ONE authored set. CanvasMovingBand appends the aria-hidden loop copy itself,
                  so a second authored copy only served 22 more logos that the rail never needs. */}
              <div className="k-kuxor">
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-new-york-post.png" alt="New York Post" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-yahoo.svg" alt="Yahoo" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-aol.svg" alt="AOL" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-food-and-wine.svg" alt="Food and Wine" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-mirror.svg" alt="Mirror" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-express.png" alt="Express" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-wikihow.svg" alt="WikiHow" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-chowhound.svg" alt="Chowhound" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-daily-dot.svg" alt="Daily Dot" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-the-daily-meal.svg" alt="The Daily Meal" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-first-for-women.png" alt="First for Women" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-daily-record.svg" alt="Daily Record" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-northjersey.svg" alt="northjersey.com" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-rupa-health.svg" alt="Rupa Health" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-grokipedia.png" alt="Grokipedia" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-the-fact-site.png" alt="The Fact Site" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-daily-dish.png" alt="Daily Dish" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-a-pinch-of-healthy.png" alt="A Pinch of Healthy" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-cupcakes-and-cutlery.png" alt="Cupcakes and Cutlery" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-food-huggers.png" alt="Food Huggers" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-foodnhotelasia.png" alt="Food and Hotel Asia" />
                {' '}
                <img loading="lazy" decoding="async" className="presslogo" src="/homepage-2026/canvas/press-uk-com.svg" alt="uk.com" />
              </div>
            </div>
          </CanvasMovingBand>
          {' '}
          {/* FRIDGE-1110 (owner: "Present better") — the catalog numbers were one
              centered run-on wrapping into ragged lines on a phone. Even grid, 2-up on
              phones and 4-up from 640px; the numbers and wording are untouched. */}
          <div className="statgrid">
            <div><b>{SITE_STATS.products}</b><span>products</span></div>
            <div><b>{SITE_STATS.brands}</b><span>brands</span></div>
            <div><b>90</b><span>free tools</span></div>
            <div><b>25+</b><span>retailers compared</span></div>
          </div>
        </div>
      </div>
    </>
  )
}

/** STAFF PICKS (heritage banner as the section header row + curated rail — owner Round 12) */
export function SecStaffPicks({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      {/* FRIDGE-1274 (owner, 09/26/2026: "change entirely to somehtnig else. use
          real link, etc"): the "How We Pick / Shop The Curated List" band only
          restated the rail below it and jumped to an anchor on this
          page. Its replacement sends a shopper somewhere real that the homepage
          did not link at all: the recall record every fridge owner should check. */}
      <div className="wrap" id="recall-check" style={{ marginTop: '48px' }}>
        <div className="recall-band">
          <div className="recall-band-copy">
            <span className="recall-band-eyebrow">Recall check</span>
            <h2>Is your fridge affected?</h2>
            <p>Find recalls, safety notices and remedies by brand.</p>
          </div>
          <A className="recall-band-cta" href={L('/recalls')}>Check your brand →</A>
        </div>
      </div>
      <div className="wrap" id="staffpicks" style={{ marginTop: '44px', scrollMarginTop: '110px' }}>
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingBottom: '4px' }}>
          <h2 className="sect-h railhead-title" style={{ margin: '0' }}>Staff Picks</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          Hand-picked by the Fridge.com team across every type and budget — <A href={L('/about-us/methodology#how-we-pick')} style={{ textDecoration: 'underline' }}>how we pick</A>.
        </div>
          <A className="viewall railhead-link" href={L('/collections/refrigerators')}>Browse all refrigerators</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-staff']}</div>
      </div>
      <div className="wrap" style={{ marginTop: '44px' }}>
        <div className="k-2kxyq">
          <div style={{ borderRadius: '16px', overflow: 'hidden', aspectRatio: '3 / 2', width: 'min(100%, 540px)', flex: '1 1 420px', boxShadow: '0 18px 40px -18px rgba(0, 0, 0, 0.3)' }}>
            <img loading="lazy" decoding="async" src="/homepage-2026/canvas/ch-older-couple.jpg" alt="Couple cooking dinner together in their upgraded kitchen" className="k-120l3" />
          </div>
          {' '}
          <div style={{ background: '#fafafa', borderRadius: '16px', boxShadow: '0 18px 40px -18px rgba(0, 0, 0, 0.25)', padding: 'clamp(28px, 3.5vw, 48px) clamp(28px, 5vw, 72px)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center', maxWidth: '620px', flex: '1 1 380px' }}>
            <span className="k-24rs5">Stay Cool</span>
            {' '}
            <span className="k-1keq1">Love Your Upgrade</span>
            {' '}
            {/* FRIDGE-808: the approved tagline stays on the homepage; FRIDGE-1274 moved it here from the band it replaced. */}
            <span className="k-808tg">A little research. A better fridge decision.</span>
            {' '}
            {/* FRIDGE-1274: real pages, not an in-page jump back up to the rail. */}
            <A href={L('/about-us/methodology#how-we-pick')} style={{ fontSize: '17px', color: '#0454c0' }}>How the Fridge.com team picks.</A>
            {' '}
            <A href={L('/best-refrigerators')} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: '8px', padding: '15px 32px', background: '#ff9951', color: 'var(--color-brand-action-text)', fontSize: '16px', fontWeight: '800', borderRadius: '10px', textDecoration: 'none' }}>See The Best Refrigerators</A>
          </div>
        </div>
      </div>
    </>
  )
}

/** SHOP BY SCENARIO */
export function SecShopByScenario({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        <div className="k-psy93">
          <h2 className="sect-h" style={{ margin: '0' }}>Shop fridges and freezers by scenario</h2>
          {' '}
          <span className="k-2mb5u">
            <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
            </span>
          </span>
        </div>
        {' '}
        <div className="rail">
          <A className="cattile" aria-label="Shop Families of 4+" href={L('/collections/refrigerators-for-large-family')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-samsung-rf32cg5900sr-1.jpg" alt="" />
            </span>
            <span className="catlab">Families of 4+</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Small Kitchens refrigerators" href={L('/collections/small-kitchen-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-ge-gle12hspss-1.jpg" alt="" />
            </span>
            <span className="catlab">Small Kitchens</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Garage Ready refrigerators" href={L('/collections/garage-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-conserv-asset-1.jpg" alt="" />
            </span>
            <span className="catlab">Garage Ready</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Dorm Rooms fridges" href={L('/collections/dorm-mini-fridges')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-sweetcrispy-asset-1.jpg" alt="" />
            </span>
            <span className="catlab">Dorm Rooms</span>
          </A>
          {' '}
          <A className="cattile" href={L('/luxury-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-bosch-b36cl80sn-1.jpg" alt="" />
            </span>
            <span className="catlab">Luxury Kitchens</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Commercial Kitchens refrigerators" href={L('/collections/commercial-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-nafcoool-asset-2.jpg" alt="" />
            </span>
            <span className="catlab">Commercial Kitchens</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Wine Collectors fridges" href={L('/collections/wine-fridges')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-ge-profile-pws06dspss-1.jpg" alt="" />
            </span>
            <span className="catlab">Wine Collectors</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Quiet Appliances refrigerators" href={L('/collections/quiet-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-summit-alfz53-1.jpg" alt="" />
            </span>
            <span className="catlab">Quiet Appliances</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Rentals refrigerators" href={L('/collections/apartment-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/equator-compact.jpg" alt="" />
            </span>
            <span className="catlab">Rentals</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Cabin & Off-Grid refrigerators" href={L('/collections/rv-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-koolmore-asset-1.jpg" alt="" />
            </span>
            <span className="catlab">Cabin &amp; Off-Grid</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Outdoor & Garage refrigerators" href={L('/collections/outdoor-refrigerators')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-kegco-hk38ssc-l-2-1.jpg" alt="" />
            </span>
            <span className="catlab">Outdoor &amp; Garage</span>
          </A>
          {' '}
          <A className="cattile" aria-label="Shop Budget Remodels" href={L('/collections/refrigerators-under-1500')}>
            <span className="tilebg">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-antarctic-star-asset-1.jpg" alt="" />
            </span>
            <span className="catlab">Budget Remodels</span>
          </A>
        </div>
      </div>
    </>
  )
}

/** FRIDGE.COM GUIDES (real registry set) */
export function SecFridgeComGuides({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        {/* FRIDGE-1099 (owner: "Put them on scroller"): the eight guide cards were a
            stacked grid that collapsed to one column on phones — measured 1626px of
            block height at a 375px viewport, two full screens of scrolling. They now
            ride the shared `.rail` scroller behind the standard `.railhead`
            (docs/Design.md §22.1), so RailNavBinder wires the arrows to the nearest
            following `.rail` exactly as it does for every other rail on the canvas.
            The rail is a manual scroller, not a moving carousel, so it carries
            directional controls and no Pause or Resume buttons (AGENTS.md). */}
        <div className="@container">
        <div className="railhead" style={{ paddingBottom: '14px' }}>
          <h2 id="guides" className="sect-h railhead-title" style={{ margin: '0' }}>Fridge.com Guides</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <A className="sect-sub railhead-link" href={L('/about-us/methodology#how-we-pick')}>Buying guides · How we pick</A>
        </div>
        </div>
        {' '}
        <div className="rail guiderail">
          <A className="vcard" href={L('/best-refrigerators')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/best-refrigerators.jpg" alt="Parents and child preparing food together in a bright kitchen" width="800" height="500" style={{ objectPosition: '50% 50%' }} />
            <span className="vtitle">Best Refrigerators 2026</span>
            <span className="vdesc">Compare refrigerator types, features, and tracked prices.</span>
            <span className="vlink">Read the guide →</span>
          </A>
          {' '}
          <A className="vcard" href={L('/best-freezers')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/best-freezers.jpg" alt="Family preparing a meal around a kitchen island" width="800" height="500" style={{ objectPosition: '50% 50%' }} />
            <span className="vtitle">Best Freezers 2026</span>
            <span className="vdesc">Highest capacity and reliability, chest and upright.</span>
            <span className="vlink">Read the guide →</span>
          </A>
          {' '}
          <A className="vcard" href={L('/refrigerator-sizing-guide')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/sizing.jpg" alt="Couple planning together with a tablet at the kitchen counter" width="800" height="500" style={{ objectPosition: '50% 50%' }} />
            <span className="vtitle">Refrigerator Sizing Guide</span>
            <span className="vdesc">How to measure your opening and fit perfectly, the first time.</span>
            <span className="vlink">Read the guide →</span>
          </A>
          {' '}
          <A className="vcard" href={L('/energy-efficiency-guide')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/energy.jpg" alt="Mother and child preparing food beside their kitchen refrigerator" width="800" height="500" style={{ objectPosition: '50% 58%' }} />
            <span className="vtitle">Energy Efficiency Guide</span>
            <span className="vdesc">Cut your electric bill with the right ENERGY STAR pick.</span>
            <span className="vlink">Read the guide →</span>
          </A>
          {' '}
          <A className="vcard" href={L('/refrigerator-buying-guide')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/buying-refrigerator.jpg" alt="Mother and children making a meal in their kitchen" width="800" height="500" style={{ objectPosition: '50% 45%' }} />
            <span className="vtitle">Refrigerator Buying Guide</span>
            <span className="vdesc">Everything you need to know before you buy.</span>
            <span className="vlink">Read the guide →</span>
          </A>
          {' '}
          <A className="vcard" href={L('/freezer-buying-guide')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/buying-freezer.jpg" alt="Parents and toddler preparing fruit together in a kitchen" width="800" height="500" style={{ objectPosition: '50% 42%' }} />
            <span className="vtitle">Freezer Buying Guide</span>
            <span className="vdesc">Which freezer type is right for your space and habits?</span>
            <span className="vlink">Read the guide →</span>
          </A>
          {' '}
          <A className="vcard" href={L('/best-specialty-appliances')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/specialty.jpg" alt="Two people unpacking groceries at the kitchen counter" width="800" height="500" style={{ objectPosition: '50% 45%' }} />
            <span className="vtitle">Best Specialty Appliances</span>
            <span className="vdesc">Wine coolers, beverage centers &amp; ice makers worth it.</span>
            <span className="vlink">Read the guide →</span>
          </A>
          {' '}
          <A className="vcard" href={L('/food-storage-appliances')}>
            <img className="guidephoto" loading="lazy" decoding="async" src="/homepage-2026/guides/food-storage.jpg" alt="Parents and toddler with fresh food on a kitchen counter" width="800" height="500" style={{ objectPosition: '50% 60%' }} />
            <span className="vtitle">Food Storage Appliances</span>
            <span className="vdesc">Cold storage for every need, from dorm to garage.</span>
            <span className="vlink">Read the guide →</span>
          </A>
        </div>
        <progress className="guideprogress" aria-label="Guide rail position" max={8} value={1} />
      </div>
    </>
  )
}

/** UNDER-THE-COUNTER — two rows (owner Round 15): fridges, then freezers */
export function SecUnderTheCounterTwoRowsFridgesThenFreezers({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingBottom: '4px' }}>
          <h2 className="sect-h railhead-title" style={{ margin: '0' }}>Shop Under-the-counter fridges</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          Built-in and under-table cooling &mdash; undercounter fridges, beverage centers and wine coolers that slide beneath the counter.
        </div>
          <A className="viewall railhead-link" href={L('/collections/undercounter-refrigerators')}>Shop all undercounter fridges</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-undercounter-fridges']}</div>
        {' '}
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingTop: '34px', paddingBottom: '4px' }}>
          <h2 className="sect-h railhead-title" style={{ margin: '0' }}>Shop Under-the-counter freezers</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          Undercounter and drawer freezers, ADA heights included &mdash; frost-free bulk cold where a chest will not fit.
        </div>
          <A className="viewall railhead-link" href={L('/collections/undercounter-freezers')}>Shop all undercounter freezers</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-undercounter-freezers']}</div>
      </div>
    </>
  )
}

/** BROWSE BY CATEGORY (real set) */
export function SecBrowseByCategory({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" id="browse-categories" style={{ paddingTop: '44px' }}>
        <div className="k-psy93">
          <h2 className="sect-h" style={{ margin: '0' }}>Browse refrigeration by category</h2>
          {' '}
          <A className="viewall" href={L('/collections/all')}>View all categories</A>
        </div>
        {' '}
        <div className="k-9lkwq">
          <A className="ctile" href={L('/best-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-award" />
              </svg>
            </span>
            Best Refrigerators
          </A>
          {' '}
          <A className="ctile" href={L('/best-freezers')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-snow" />
              </svg>
            </span>
            Best Freezers
          </A>
          {' '}
          <A className="ctile" href={L('/french-door-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-home" />
              </svg>
            </span>
            French Door
          </A>
          {' '}
          <A className="ctile" href={L('/side-by-side-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-compare" />
              </svg>
            </span>
            Side by Side
          </A>
          {' '}
          <A className="ctile" href={L('/mini-fridges')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-min" />
              </svg>
            </span>
            Mini Fridges
          </A>
          {' '}
          <A className="ctile" href={L('/counter-depth-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-ruler" />
              </svg>
            </span>
            Counter Depth
          </A>
          {' '}
          <A className="ctile" href={L('/energy-efficient-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-leaf" />
              </svg>
            </span>
            Energy Efficient
          </A>
          {' '}
          <A className="ctile" href={L('/smart-fridges')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-cpu" />
              </svg>
            </span>
            Smart Fridges
          </A>
          {' '}
          <A className="ctile" href={L('/large-capacity-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-max" />
              </svg>
            </span>
            Large Capacity
          </A>
          {' '}
          <A className="ctile" href={L('/quiet-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-vol" />
              </svg>
            </span>
            Quiet Fridges
          </A>
          {' '}
          <A className="ctile" href={L('/refrigerator-buying-guide')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-book" />
              </svg>
            </span>
            Buying Guide
          </A>
          {' '}
          <A className="ctile" href={L('/freezer-buying-guide')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-book" />
              </svg>
            </span>
            Freezer Guide
          </A>
          {' '}
          <A className="ctile" href={L('/energy-efficiency-guide')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-bolt" />
              </svg>
            </span>
            Energy Guide
          </A>
          {' '}
          <A className="ctile" href={L('/wine-beverage-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-wine" />
              </svg>
            </span>
            Wine &amp; Beverage
          </A>
          {' '}
          <A className="ctile" href={L('/commercial-refrigerators')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-build" />
              </svg>
            </span>
            Commercial
          </A>
          {' '}
          <A className="ctile" href={L('/filters#water-filters')}>
            <span className="cico">
              <svg className="i">
                <use href="#i-drop" />
              </svg>
            </span>
            Water Filters
          </A>
        </div>
      </div>
    </>
  )
}

/** DATA DESK BAND */
export function SecDataDeskBand({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        <div className="k-psy93">
          <h2 className="sect-h" style={{ margin: '0' }}>From the Fridge.com data desk</h2>
          {' '}
          <A className="viewall" href={L('/press')}>Press &amp; research</A>
        </div>
        {' '}
        <div className="k-12dc2">
          <div className="datacard" style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
            <div className="k-1zwhw">
              <svg className="i20" style={{ color: '#0469f0' }}>
                <use href="#i-chart" />
              </svg>
              <span className="k-yd68w">Appliance Price Index</span>
            </div>
            {' '}
            <span className="k-1dfaq">
              Matched-model refrigerator price trends, built from our own catalog — how prices actually move month over month.
            </span>
            {' '}
            <svg viewBox="0 0 120 36" preserveAspectRatio="none" className="k-7zo3e">
              <polyline className="pi-line-base" aria-hidden="true" points="2,8 18,10 32,9 46,15 60,14 74,20 88,19 102,26 118,30" fill="none" stroke="#0469f0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <polyline className="pi-line pi-line-motion" pathLength={1} points="2,8 18,10 32,9 46,15 60,14 74,20 88,19 102,26 118,30" fill="none" stroke="#0469f0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              {/* Keep the existing glint and endpoint pulse while the owner-requested
                  line redraw runs over a faint complete trace. Decorative. */}
              <polyline className="pi-glint" aria-hidden="true" points="2,8 18,10 32,9 46,15 60,14 74,20 88,19 102,26 118,30" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle className="pi-halo" aria-hidden="true" cx="118" cy="30" r="3" fill="#0469f0" />
              <circle className="pi-dot" cx="118" cy="30" r="3" fill="#0469f0" />
            </svg>
            {' '}
            <A href={L('/data')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Explore the data</A>
          </div>
          {' '}
          <div className="datacard" style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
            <div className="k-1zwhw">
              <svg className="i20" style={{ color: '#dc2626' }}>
                <use href="#i-shield" />
              </svg>
              <span className="k-yd68w">Recall Alerts</span>
            </div>
            {' '}
            <span className="k-1dfaq">
              Refrigerator and freezer safety recalls, tracked continuously — check any model before you buy or keep using it.
            </span>
            {' '}
            <span className="k-j868n">{slots['datadesk-recalls']}</span>
            {' '}
            <A href={L('/recalls')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Check for recalls</A>
          </div>
          {' '}
          <div className="datacard" style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
            <div className="k-1zwhw">
              <svg className="i20" style={{ color: '#0e8a4d' }}>
                <use href="#i-tag" />
              </svg>
              <span className="k-yd68w">Utility Rebates</span>
            </div>
            {' '}
            <span className="k-1dfaq">
              A verified rebate directory across participating states — cash back for efficient appliances and old-fridge recycling.
            </span>
            {' '}
            <span className="k-j868n">Programs verified state by state</span>
            {' '}
            <A href={L('/rebates')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Find rebates near you</A>
          </div>
          {' '}
          <div className="datacard" style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
            <div className="k-1zwhw">
              <svg className="i20" style={{ color: '#0e8a4d' }}>
                <use href="#i-leaf" />
              </svg>
              <span className="k-yd68w">ENERGY STAR Matches</span>
            </div>
            {' '}
            <span className="k-1dfaq">
              Every model is cross-referenced against EPA ENERGY STAR certified-product data — 400+ certified refrigerators and freezers flagged across the catalog.
            </span>
            {' '}
            <span className="k-j868n">Certified picks marked on every rail</span>
            {' '}
            <A href={L('/collections/energy-star-refrigerators')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Shop certified picks</A>
          </div>
        </div>
        {' '}
        <div className="@container" style={{ marginTop: '18px' }}>
        <div className="flex flex-wrap items-center gap-3 @max-[640px]:grid @max-[640px]:grid-cols-2 @max-[640px]:gap-2" style={{ background: '#f7fafd', border: '1px solid #e8eef5', borderRadius: '12px', padding: '16px 18px' }}>
          {/* FRIDGE-1101 (owner: "Looks sloppy"): variable-width flag chips wrapped into ragged
              rows on phones with "All 50 states + DC" hanging off the last one. Under 640px of
              container width the label spans the row, the chips are an even two-column grid
              (flag left, name truncating), and the link fills the 14th cell — right-aligned beside the
              13th state, so the grid has no orphan cell (docs/Design.md §22.2). */}
          <span className="@max-[640px]:col-span-2" style={{ fontSize: '13.5px', fontWeight: '700', color: '#101828', flex: 'none' }}>
            <svg className="i" style={{ width: '15px', height: '15px', verticalAlign: '-2px', color: '#0469f0' }}>
              <use href="#i-pin" />
            </svg>
            {' '}<LocalNearYou generic="Rebates & energy costs near you:" />
          </span>
          {' '}
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/california')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-california.png" alt="California state flag" />
            California
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/texas')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-texas.png" alt="Texas state flag" />
            Texas
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/florida')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-florida.png" alt="Florida state flag" />
            Florida
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/new-york')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-new-york.png" alt="New York state flag" />
            New York
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/illinois')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-illinois.png" alt="Illinois state flag" />
            Illinois
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/pennsylvania')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-pennsylvania.png" alt="Pennsylvania state flag" />
            Pennsylvania
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/ohio')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-ohio.png" alt="Ohio state flag" />
            Ohio
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/georgia')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-georgia.png" alt="Georgia state flag" />
            Georgia
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/north-carolina')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-north-carolina.png" alt="North Carolina state flag" />
            North Carolina
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/michigan')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-michigan.png" alt="Michigan state flag" />
            Michigan
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/new-jersey')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-new-jersey.png" alt="New Jersey state flag" />
            New Jersey
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/virginia')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-virginia.png" alt="Virginia state flag" />
            Virginia
          </A>
          <A className="chip stchip @max-[640px]:min-w-0 @max-[640px]:truncate @max-[640px]:justify-start" href={L('/local/washington')}>
            <img loading="lazy" decoding="async" className="stflag" src="/homepage-2026/canvas/flag-washington.png" alt="Washington state flag" />
            Washington
          </A>
          {' '}
          <A className="@max-[640px]:justify-self-end @max-[640px]:self-center" href={L('/local')} style={{ fontSize: '13px', fontWeight: '600', marginLeft: 'auto', whiteSpace: 'nowrap' }}>All 50 states + DC →</A>
        </div>
        </div>
      </div>
    </>
  )
}

/** TOP / BOTTOM FREEZER MARQUEES (owner Round 14: the Price-drops opposite-moving style, under the data desk) */
export function SecTopBottomFreezerMarquees({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        <div className="k-2clpu">
          <h2 className="sect-h" style={{ margin: '0' }}>Shop Top freezer refrigerators</h2>
          {' '}
          <A className="viewall" href={L('/collections/top-freezer-refrigerators')}>Shop all top freezer</A>
        </div>
        {' '}
        <div className="sect-sub" style={{ paddingBottom: '14px' }}>The classic layout &mdash; every price is the lowest across tracked retailers today.</div>
        {' '}
        <div className="dealmarq">{slots['marquee-topfreezer']}</div>
        {' '}
        <div className="k-2e7o1">
          <h2 className="sect-h" style={{ margin: '0' }}>Shop Bottom freezer refrigerators</h2>
          {' '}
          <A className="viewall" href={L('/collections/bottom-freezer-refrigerators')}>Shop all bottom freezer</A>
        </div>
        {' '}
        <div className="sect-sub" style={{ paddingBottom: '14px' }}>
          Fresh food at eye level &mdash; every price is the lowest across tracked retailers today.
        </div>
        {' '}
        <div className="dealmarq">{slots['marquee-bottomfreezer']}</div>
      </div>
    </>
  )
}

/** BUSINESS & COMMERCIAL — the Fun Moments block for commercial (owner Round 12; moved under the data desk, owner Round 13) */
export function SecBusinessCommercialTheFunMomentsBlockForCommercial({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '48px' }}>
        <div className="k-1by0y">
          <span className="k-1wjzr">Business &amp; Commercial</span>
          {' '}
          <h2 className="k-269yi">Be Ready For The Rush</h2>
        </div>
        {' '}
        <div className="k-oq3d5">
          {/* FRIDGE-1277 (owner: "move this first"): bar coolers lead the band. */}
          <A className="funcard" href={L('/collections/commercial-bar-coolers')}>
            <div className="k-12bvw">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/px-busy-bar-1267351.jpg" alt="Busy bar with the bartender pouring a pint" className="k-120l3" />
            </div>
            {' '}
            <span className="funtitle">Bar Coolers &amp; Kegerators</span>
            {' '}
            <span className="fundesc">
              Back-bar coolers and draft kegerators that keep the taproom pouring cold from open to close.
            </span>
          </A>
          {' '}
          <A className="funcard" href={L('/collections/commercial-display-cases')}>
            <div className="comtile">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/fridge-com-nafcoool-asset-2.jpg" alt="NAFCOOL three-glass-door merchandiser" className="k-3mkdk" />
            </div>
            {' '}
            <span className="funtitle">Display Coolers</span>
            {' '}
            <span className="fundesc">Glass-door merchandisers that sell what is inside, LED-lit and sized for any aisle.</span>
          </A>
          {' '}
          {/* FRIDGE-1277 (owner: "move this last and choose photo commercial refrigerator people
              working in commercial kitchen"): full-bleed photo frame like the bar card. Photo:
              Pexels 5779775 by RDNE Stock project, Pexels License (free for commercial use). */}
          <A className="funcard" href={L('/commercial-refrigerators')}>
            <div className="k-12bvw">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/px-commercial-kitchen-5779775.jpg" alt="Two cooks working at a refrigerated prep station in a commercial kitchen" className="k-120l3" />
            </div>
            {' '}
            <span className="funtitle">Commercial Refrigerators</span>
            {' '}
            <span className="fundesc">
              Reach-in and undercounter units built to run all day, price-tracked across retailers.
            </span>
          </A>
        </div>
      </div>
    </>
  )
}

/** FIND YOUR FRIDGE — compact, working */
export function SecFindYourFridgeCompactWorking({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div id="quiz" className="k-1y1t8">
        <div className="wrap" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '28px', paddingTop: '36px', paddingBottom: '40px', alignItems: 'center' }}>
          <div className="k-da0df">
            <span className="quiz-mono" style={{ color: '#ff9951' }}>QUIZ</span>
            {' '}
            <h2 className="k-18omi">
              {QUIZ_COPY.heading}
            </h2>
            {' '}
            <p className="k-2r497">
              {QUIZ_COPY.supporting}
            </p>
            {' '}
            <div className="k-13a2c">
              Five questions
              <span>·</span>
              Product groups based on your answers
            </div>
            {' '}
            <div className="k-krksg">
              <A href="#quiz-questions" style={{ background: '#ff9951', color: '#101828', fontWeight: '700', fontSize: '14px', padding: '11px 24px', borderRadius: '9999px', textDecoration: 'none', flex: 'none' }}>Take the quiz &rarr;</A>
              {' '}
              <span className="k-qg92i">No account. Join {SITE_STATS.users} cool shoppers.</span>
            </div>
            {' '}
            <div className="k-2fhhd">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/wb-couple.jpg" alt="Couple cooking beside their refrigerator" className="k-120l3" />
            </div>
          </div>
          {' '}
          <div id="quiz-questions" tabIndex={-1} className="quiz-card scroll-mt-24" style={{ padding: '28px 30px', minHeight: '420px' }}>{slots['quiz-card']}</div>
        </div>
      </div>
    </>
  )
}

/** GARAGE REFRIGERATORS (scroller, owner Round 14: under the quiz) */
export function SecGarageRefrigerators({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingBottom: '4px' }}>
          <h2 className="sect-h railhead-title" style={{ margin: '0' }}>Shop Garage refrigerators</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          Garage-ready fridges and freezers rated for temperature swings — price-tracked across retailers.
        </div>
          <A className="viewall railhead-link" href={L('/garage-refrigerators')}>Shop all garage ready</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-garage']}</div>
      </div>
    </>
  )
}

/** FREE TOOLS */
export function SecFreeTools({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '48px' }}>
        <div className="k-psy93">
          <h2 id="tools" className="sect-h" style={{ margin: '0' }}>Free tools that prevent expensive mistakes</h2>
          {' '}
          <A className="viewall" href={L('/tools')}>All {SITE_STATS.tools} free tools</A>
        </div>
        {' '}
        {/* FRIDGE-1104 (owner: "For better and animate"): the eight tool cards collapsed
            to ONE column under a 240px minimum track, so on a phone they were a
            343x100px centred card stacked eight deep — measured 886px of block height
            at a 375px viewport, taller than the 812px screen. `.toolgrid` keeps the
            desktop track and drops to an even two-column grid on phones (canvas.css,
            the `.qgrid` pattern), so the row count halves and nothing is ragged. */}
        <div className="toolgrid">
          <A className="toolcard" href={L('/tools/refrigerator-size-calculator')}>
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-ruler" />
            </svg>
            <span className="catlab">Size Calculator</span>
            <span className="k-vfhjn">Right fit for your kitchen</span>
          </A>
          {' '}
          <A className="toolcard" href={L('/tools/energy-cost-calculator')}>
            <svg className="i20">
              <use href="#i-leaf" />
            </svg>
            <span className="catlab">Energy Cost Calculator</span>
            <span className="k-vfhjn">Annual running cost</span>
          </A>
          {' '}
          <A className="toolcard" href={L('/tools/find-your-fridge')}>
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-search" />
            </svg>
            <span className="catlab">Find Your Fridge</span>
            <span className="k-vfhjn">Personalized quiz</span>
          </A>
          {' '}
          <A className="toolcard" href={L('/tools/kitchen-space-planner')}>
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-home" />
            </svg>
            <span className="catlab">Kitchen Space Planner</span>
            <span className="k-vfhjn">Measure your layout</span>
          </A>
          {' '}
          <A className="toolcard" href={L('/tools/refrigerator-lifespan-calculator')}>
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-clock" />
            </svg>
            <span className="catlab">Lifespan Calculator</span>
            <span className="k-vfhjn">How long will it last?</span>
          </A>
          {' '}
          <A className="toolcard" href={L('/tools/refrigerator-recall-checker')}>
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-shield" />
            </svg>
            <span className="catlab">Recall Checker</span>
            <span className="k-vfhjn">Active safety recalls</span>
          </A>
          {' '}
          <A className="toolcard" href={L('/rebates')}>
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-tag" />
            </svg>
            <span className="catlab">Rebate Finder</span>
            <span className="k-vfhjn">Programs near you</span>
          </A>
          {' '}
          <A className="toolcard" href={L('/tools/refrigerator-repair-vs-replace-calculator')}>
            <svg className="i20" style={{ color: '#0469f0' }}>
              <use href="#i-compare" />
            </svg>
            <span className="catlab">Repair vs. Replace</span>
            <span className="k-vfhjn">When to buy new</span>
          </A>
        </div>
      </div>
    </>
  )
}

/** THE FRIDGE.COM PROMISE (heritage split, original photo) */
// FRIDGE-1105: the Promise section no longer links anywhere, so it does not
// destructure the link gate. The prop stays in the type because every section
// in SECTION_ORDER is called with the same shape.
export function SecTheFridgeComPromise({ slots }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '48px' }}>
        {/* FRIDGE-1106 (owner: "Center better"): the copy column's desktop indent
            (clamp(0px, 5vw, 72px)) survived into the stacked phone layout, so
            "Find your fridge. Keep your cool." started 19px right of the photo's edge. Under
            784px of container width (where the two-column grid stacks) the indent
            is 0 and the seal row is centred over the photo. */}
        <div className="@container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 'clamp(24px, 4vw, 56px)', alignItems: 'center' }}>
          <div className="pl-[clamp(0px,5vw,72px)] @max-[784px]:pl-0" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <span className="k-1wjzr">The Fridge.com Promise</span>
            {' '}
            <h2 className="k-269yi">Relax, it&#39;s all free</h2>
            {' '}
            <div className="k-25jwa">
              <svg className="i20" style={{ color: '#0469f0', flex: 'none' }}>
                <use href="#i-compare" />
              </svg>
              <span className="k-k4zgf">We compare prices from {RETAILER_INTEGRATIONS_DISPLAY} retailers so you don’t have to.</span>
            </div>
            {' '}
            <div className="k-25jwa">
              <svg className="i20" style={{ color: '#0469f0', flex: 'none' }}>
                <use href="#i-chart" />
              </svg>
              <span className="k-k4zgf">See recorded price history to put today’s offers in context.</span>
            </div>
            {' '}
            <div className="k-25jwa">
              <svg className="i20" style={{ color: '#0469f0', flex: 'none' }}>
                <use href="#i-shield" />
              </svg>
              <span className="k-k4zgf">Recall alerts, rebates, and energy costs included.</span>
            </div>
            {' '}
            <div className="k-25jwa">
              <svg className="i20" style={{ color: '#0469f0', flex: 'none' }}>
                <use href="#i-heart" />
              </svg>
              <span className="k-k4zgf">Less searching. More time for your family — furry members included.</span>
            </div>
            {' '}
            {/* FRIDGE-805: optional ZIP → that ZIP's local page (rebates, ¢/kWh, FIS). Plain GET
                form, works without JavaScript; nothing stored, no cookie. The label both names the
                field (htmlFor) and wraps it (nesting); the button stays outside the label because a
                button is itself labelable and may not sit inside another control's label. */}
            <form action="/api/local/zip" method="get" className="promise-zip">
              <div className="promise-zip-grid">
                <label htmlFor="hp-zip" className="promise-zip-field">
                  <span className="promise-zip-title">Explore savings near you</span>
                  <input id="hp-zip" name="zip" type="text" inputMode="numeric" autoComplete="postal-code" pattern="[0-9]{5}" maxLength={5} placeholder="ZIP code" aria-label="ZIP code" required />
                </label>
                <button type="submit">Find savings →</button>
              </div>
            </form>
            <div className="k-23ika">
              <div className="promise-seal" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span className="k-eelnq">Find your fridge. Keep your cool.</span>
              </div>
            </div>
          </div>
          {' '}
          <div className="k-bar5p">
            <img loading="lazy" decoding="async" src="/homepage-2026/canvas/ch-man-and-dog.jpg" alt="A very good dog greeting his favorite person" className="k-120l3" />
          </div>
        </div>
      </div>
    </>
  )
}

/** APARTMENT-SIZE REFRIGERATORS (FRIDGE-1080, owner 09/17/2026: "add row of apartment size refrigerators" — the band between the promise and Quick answers) */
export function SecApartmentRefrigerators({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" id="apartment-refrigerators" style={{ marginTop: '44px', scrollMarginTop: '110px' }}>
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingBottom: '4px' }}>
          <h2 className="sect-h railhead-title" style={{ margin: '0' }}>Shop apartment-size refrigerators</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          Narrow and slim full-size refrigerators for small kitchens — price-tracked across major retailers.
        </div>
          <A className="viewall railhead-link" href={L('/collections/apartment-refrigerators')}>Shop all apartment refrigerators</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-apartment']}</div>
      </div>
    </>
  )
}

/** QUICK ANSWERS */
export function SecQuickAnswers({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px', paddingBottom: '48px' }}>
        <div className="k-psy93">
          <h2 className="sect-h" style={{ margin: '0' }}>Quick answers from Fridge.com</h2>
          {' '}
          <A className="viewall" href={L('/refrigerator-buying-guide')}>All buying guides</A>
        </div>
        {' '}
        {/* FRIDGE-1108 (owner 09/18/2026: "Make two rows of chips moving in opposite
            directions"). The eight cards are unchanged and unmoved; they are split four
            and four into two row wrappers. Above the phone breakpoint both wrappers are
            `display: contents`, so these are still eight direct grid items of .qgrid and
            the subgrid rule is untouched. `qchipgrid` is what scopes every new rule away
            from the FIN, FPN, FFN and FIS explainers, which share .qgrid and .qcard. */}
        <div className="qgrid qchipgrid">
          <div className="qchiprow">
          <div className="qchiptrack">
          <div className="qcard">
            <span className="qq">What size refrigerator does a family of 4 need?</span>
            {' '}
            <span className="qa">{FAMILY_OF_FOUR_QUICK_ANSWER.answer}</span>
            {' '}
            <A href={L('/tools/refrigerator-size-calculator')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Refrigerator size calculator</A>
          </div>
          {' '}
          <div className="qcard">
            <span className="qq">Is a French door or side-by-side refrigerator better?</span>
            {' '}
            <span className="qa">
              French door: wider shelves and better fresh-food organization. Side-by-side: easier frozen access in narrow kitchens.
            </span>
            {' '}
            <A href={L('/tools/french-door-vs-side-by-side-refrigerator')} style={{ fontSize: '12.5px', fontWeight: '600' }}>French door vs side-by-side comparison</A>
          </div>
          {' '}
          <div className="qcard">
            <span className="qq">When is the best time to buy a refrigerator?</span>
            {' '}
            <span className="qa">
              Price drops cluster around May–July and the winter holidays. A 30-day price history shows whether a price is a real deal.
            </span>
            {' '}
            <A href={L('/deals')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Today&#39;s refrigerator deals</A>
          </div>
          {' '}
          <div className="qcard">
            <span className="qq">Are ENERGY STAR refrigerators worth it?</span>
            {' '}
            <span className="qa">
              ENERGY STAR refrigerators typically save $50–$100+ per year on electricity. Local utility rebates can take hundreds more off up front.
            </span>
            {' '}
            <A href={L('/energy-efficiency-guide')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Energy efficiency guide</A>
          </div>
          </div>
          </div>
          {' '}
          <div className="qchiprow qchiprow-rev">
          <div className="qchiptrack">
          <div className="qcard">
            <span className="qq">How long does a refrigerator last?</span>
            {' '}
            <span className="qa">{LIFESPAN_QUICK_ANSWER.answer}</span>
            {' '}
            <A href={L('/tools/refrigerator-lifespan-calculator')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Refrigerator lifespan calculator</A>
          </div>
          {' '}
          <div className="qcard">
            <span className="qq">What is a counter-depth refrigerator?</span>
            {' '}
            <span className="qa">
              A counter-depth refrigerator is 24–25 inches deep so the doors sit flush with standard counters. Standard-depth models run 30–36 inches deep.
            </span>
            {' '}
            <A href={L('/counter-depth-refrigerators')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Counter-depth refrigerators</A>
          </div>
          {' '}
          <div className="qcard">
            <span className="qq">How much does it cost to run a refrigerator?</span>
            {' '}
            <span className="qa">
              A modern full-size refrigerator costs roughly $4–$15 per month to run, depending on size, age, and local electricity rates.
            </span>
            {' '}
            <A href={L('/tools/energy-cost-calculator')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Energy cost calculator</A>
          </div>
          {' '}
          <div className="qcard">
            <span className="qq">What temperature should a refrigerator be set to?</span>
            {' '}
            <span className="qa">
              Keep the refrigerator at 35–38°F and the freezer at 0°F. Above 40°F, bacteria growth accelerates — that is the food-safety line.
            </span>
            {' '}
            <A href={L('/refrigerator-buying-guide')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Refrigerator buying guide</A>
          </div>
          </div>
          </div>
        </div>
        {' '}
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingTop: '34px', paddingBottom: '4px' }}>
          <h2 className="sect-h railhead-title" style={{ margin: '0' }}>Shop deep freezers</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          Chest and deep freezers from 7 to 30 cu. ft. — bulk storage, garage ready, price-tracked.
        </div>
          <A className="viewall railhead-link" href={L('/collections/deep-freezers')}>Shop all deep freezers</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-deep']}</div>
      </div>
    </>
  )
}

/** THE FOUR NUMBERS (owner Round 14: FIN / FPN / FFN / FIS prominent, one row, named) */
export function SecTheFourNumbers({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" id="identifiers" style={{ paddingTop: '44px', paddingBottom: '48px' }}>
        <div className="k-2clpu">
          <h2 className="sect-h" style={{ margin: '0' }}>FIN, FPN, FFN and FIS explained</h2>
          {' '}
          <A className="viewall" href={L('/about-us')}>How Fridge.com works</A>
        </div>
        {' '}
        <div className="sect-sub" style={{ paddingBottom: '14px' }}>
          FIN identifies refrigeration appliances, FPN identifies their replacement parts, and FFN identifies their filters. FIS is a location score, not a product identifier.
        </div>
        {' '}
        <div className="qgrid idgrid">
          <div className="qcard">
            <div className="k-1zdbl">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/qa-fin-model.jpg" alt="Refrigerator with its Fridge.com Identification Number" className="k-1jtb7" />
            </div>
            {' '}
            <span className="idbig">FIN</span>
            {' '}
            <span className="qq">What is a FIN?</span>
            {' '}
            <span className="qa">{IDENTIFIER_DEFINITIONS.FIN}</span>
            {' '}
            <A href={L('/about-us/fin')} style={{ fontSize: '12.5px', fontWeight: '600' }}>How FIN works</A>
          </div>
          {' '}
          <div className="qcard">
            <div className="k-1zdbl">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/qa-fridge-parts.jpg" alt="Refrigerator drawer with bins and shelves" className="k-1jtb7" />
            </div>
            {' '}
            <span className="idbig">FPN</span>
            {' '}
            <span className="qq">What is an FPN?</span>
            {' '}
            <span className="qa">{IDENTIFIER_DEFINITIONS.FPN}</span>
            {' '}
            <A href={L('/parts')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Browse parts by FPN</A>
          </div>
          {' '}
          <div className="qcard">
            <div className="k-1zdbl">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/qa-water-filter.jpg" alt="Refrigerator water filter replacement" className="k-1jtb7" />
            </div>
            {' '}
            <span className="idbig">FFN</span>
            {' '}
            <span className="qq">What is an FFN?</span>
            {' '}
            <span className="qa">{IDENTIFIER_DEFINITIONS.FFN}</span>
            {' '}
            <A href={L('/filters')} style={{ fontSize: '12.5px', fontWeight: '600' }}>Browse filters by FFN</A>
          </div>
          {' '}
          <div className="qcard">
            <div className="k-1zdbl">
              <img loading="lazy" decoding="async" src="/homepage-2026/canvas/qa-energy-kitchen.jpg" alt="Kitchen with a french door refrigerator" className="k-1jtb7" />
            </div>
            {' '}
            <span className="idbig">FIS</span>
            {' '}
            <span className="qq">What is FIS?</span>
            {' '}
            <span className="qa">{IDENTIFIER_DEFINITIONS.FIS}</span>
            {' '}
            <A href={L('/local')} style={{ fontSize: '12.5px', fontWeight: '600' }}>FIS by state, city and ZIP</A>
          </div>
        </div>
      </div>
    </>
  )
}

/** ENERGY STAR REFRIGERATORS (owner 09/02 — between Quick Answers and blog) */
export function SecEnergyStarRefrigerators({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px' }}>
        {/* FRIDGE-1096: title | link | arrows on one row, description under it; under 640px of
            container width the arrows stay on the title row, the description follows, and the
            "Shop all" link comes last (homepage.css .railhead). */}
        <div className="@container">
        <div className="railhead" style={{paddingBottom: '4px' }}>
          <h2 className="sect-h railhead-title" style={{ margin: '0' }}>Shop ENERGY STAR refrigerators</h2>
          <span className="railnav">
              <button type="button" aria-label="Previous">
                <svg style={{ transform: 'rotate(180deg)' }}>
                  <use href="#i-chev" />
                </svg>
              </button>
              <button type="button" aria-label="Next">
                <svg>
                  <use href="#i-chev" />
                </svg>
              </button>
          </span>
          <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>
          EPA-certified efficient — typically $50–$100+ less electricity per year, cross-referenced with ENERGY STAR product data.
        </div>
          <A className="viewall railhead-link" href={L('/collections/energy-star-refrigerators')}>Shop all ENERGY STAR</A>
        </div>
        </div>
        {' '}
        <div className="rail">{slots['rail-energystar']}</div>
      </div>
    </>
  )
}

/** LATEST FROM THE BLOG (real posts + real hero photos) */
export function SecLatestFromTheBlog({ slots, L }: { slots: Slots; L: LinkGate }) {
  return (
    <>
      <div className="wrap" style={{ paddingTop: '44px', paddingBottom: '56px' }}>
        <div className="k-1yce9">
          <h2 className="sect-h" style={{ margin: '0' }}>Fridge.com Blog</h2>
          {' '}
          <A className="viewall" href={L('/blogs/news')}>Show All Blog</A>
        </div>
        {' '}
        <div className="k-2bkil">{slots['blog-grid']}</div>
      </div>
    </>
  )
}

export const SECTION_ORDER = [SecAnnouncementStrip, SecHero, SecTrustStatBand, SecRealKitchensBand, SecCategoryTiles, SecPriceDropsVerifiedToday, SecOneOffOrangeFeatureRetro, SecExactNeedCollectionLinks, SecRefrigerators, SecFreezers, SecOneOffOrangeFeatureMiniFreezers, SecCompareBanner, SecBestBrandsRibbon, SecShopByBrand, SecShopByBudgetWidthFinish, SecSpecialtyCooling, SecFiltersParts, SecFamilyFriendsFunMoments, SecPriceIntelligence, SecStaffPicks, SecShopByScenario, SecFridgeComGuides, SecUnderTheCounterTwoRowsFridgesThenFreezers, SecBrowseByCategory, SecDataDeskBand, SecTopBottomFreezerMarquees, SecBusinessCommercialTheFunMomentsBlockForCommercial, SecFindYourFridgeCompactWorking, SecGarageRefrigerators, SecFreeTools, SecTheFridgeComPromise, SecApartmentRefrigerators, SecQuickAnswers, SecTheFourNumbers, SecEnergyStarRefrigerators, SecLatestFromTheBlog] as const
